import React from "react";
import { createRoot } from "react-dom/client";

import { createInstance, MatomoProvider } from "@jonkoops/matomo-tracker-react";
import * as serviceWorker from "serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import "index.css";
import "leaflet/dist/leaflet.css";
// perfect scrollbars!
import "react-perfect-scrollbar/dist/css/styles.css";
import "assets/scss/default-theme.scss?v=1.8.0";
import ErrorBoundary from "components/ErrorBoundary";
import { App } from "App";
import { localStoreManager } from "core/storage";
import { areCookiesSelected, isDoNotTrackEnabled } from "core/cookie";
import { CognitoAuthProvider } from "components/Authentication/CognitoAuthContext";

function docReady(fn) {
  // see if DOM is already available
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    // call on next available tick
    setTimeout(fn, 1);
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

function setupAnalytics() {
  const instance = createInstance({
    urlBase: "https://onyxinsight.matomo.cloud",
    trackerUrl: `https://onyxinsight.matomo.cloud/matomo.php`,
    srcUrl: `https://onyxinsight.matomo.cloud/matomo.js`,
    siteId: process.env.REACT_APP_MATOMO_TRACKING_MODE === "1" ? 5 : 6,
    disabled: false,
    linkTracking: false,
    heartBeat: {
      active: true,
      seconds: 10
    },
    configurations: {
      setCookieDomain: "*.onyxinsight.com",
      setDocumentTitle: document.domain + "/" + document.title,
      // https://developer.matomo.org/api-reference/tracking-javascript
      // By default the Matomo tracker assumes consent to tracking. To change this behavior
      // so nothing is tracked until a user consents, you must call requireConsent.
      requireConsent: !areCookiesSelected(),
      setRequestMethod: "POST",
      // https://matomo.org/faq/how-to/faq_23654/#
      // Domains for Cross domain tracking
      setDomains: ["*.onyxinsight.com"],
      // Redirects e.g. from, fieldpro
      enableCrossDomainLinking: true,
      // https://matomo.org/docs/privacy-how-to/#step-4-respect-donottrack-preference
      // By default, Matomo respects users preference and will not track visitors which
      // have specified “I do not want to be tracked” in their web browsers
      setDoNotTrack: isDoNotTrackEnabled()
    }
  });

  return instance;
}

docReady(() => {
  const preloader = document.getElementsByClassName("preloader");
  if (preloader && preloader.length) {
    for (const elem of preloader) {
      elem.remove();
    }
  }
});

// on app load setup the session storage syncing across tabs.
localStoreManager.initialiseStorageSyncListener();

window.addEventListener("beforeunload", () => {
  localStoreManager.disposeStorageSyncListener();
});

const matomoInstance = setupAnalytics();

const container = document.getElementById("root");
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
  <ErrorBoundary>
    <CognitoAuthProvider>
      <MatomoProvider value={matomoInstance}>
        <App />
      </MatomoProvider>
    </CognitoAuthProvider>
  </ErrorBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
//
// Note that we have removed this for now, because we found problems with
// the service worker not correctly working when downloading files in the
// background. For further context see !1126 which removed the service worker
// registration
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// todo: use matomo to store vitals. This can be done through Custom Dimensions or Custom Variables
// https://developer.matomo.org/guides/tracking-javascript-guide
// https://create-react-app.dev/docs/measuring-performance/
reportWebVitals();
